import type { LocalPresetRecord } from "../../shared/types.js";

export const LIBRARY_DELETE_NAME_PREVIEW_LIMIT = 5;

export type LibraryDeleteConfirmationCopy = {
  title: string;
  confirmLabel: string;
  description: string;
  previewItems: Array<{ id: string; name: string }>;
  remainingNameCount: number;
};

export function buildLibraryDeleteConfirmationCopy(
  snapshot: LocalPresetRecord[],
): LibraryDeleteConfirmationCopy | null {
  if (snapshot.length === 0) return null;

  const count = snapshot.length;
  const first = snapshot[0];
  if (!first) return null;

  const title = count === 1 ? `Delete “${first.name}”?` : `Delete ${count} presets?`;
  const confirmLabel = count === 1 ? "Delete preset" : `Delete ${count} presets`;
  const description =
    count === 1
      ? "This removes the preset from your local library and, when signed in, from your synced account. This cannot be undone from this UI."
      : "This removes the presets from your local library and, when signed in, from your synced account. This cannot be undone from this UI.";

  const previewItems = snapshot
    .slice(0, LIBRARY_DELETE_NAME_PREVIEW_LIMIT)
    .map((preset) => ({ id: preset.id, name: preset.name }));
  const remainingNameCount = Math.max(0, count - LIBRARY_DELETE_NAME_PREVIEW_LIMIT);

  return {
    title,
    confirmLabel,
    description,
    previewItems,
    remainingNameCount,
  };
}

export function snapshotLibraryPresetsByIds(
  library: LocalPresetRecord[],
  ids: string[],
): LocalPresetRecord[] {
  const idSet = new Set(ids);
  return library.filter((preset) => idSet.has(preset.id));
}

export function filterSnapshotIdsToPresentLibrary(
  snapshot: LocalPresetRecord[],
  library: LocalPresetRecord[],
): string[] {
  const presentIds = new Set(library.map((preset) => preset.id));
  return snapshot.map((preset) => preset.id).filter((id) => presentIds.has(id));
}

export function buildEditorDiscardConfirmationCopy() {
  return {
    title: "Discard unsaved changes?",
    description: "Your preset edits have not been saved.",
    confirmLabel: "Discard changes",
    tone: "warning" as const,
  };
}
